import React, { useState } from 'react';
import {
    Title3,
    Switch,
    tokens,
    makeStyles,
    Input,
    Dropdown,
    Option,
    Button,
    DrawerBody,
    Drawer,
    DrawerHeader,
    DrawerHeaderTitle,
} from '@fluentui/react-components';
import { Navigation24Regular, ArrowClockwise24Regular, Dismiss24Regular } from '@fluentui/react-icons';
import { StaffLanguage } from '../../types';

const useStyles = makeStyles({
    stickyHeader: {
        position: 'sticky',
        top: 0,
        zIndex: 100,
        backgroundColor: tokens.colorNeutralBackground1,
        borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    },
    navbar: {
        display: 'flex',
        alignItems: 'center',
        padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalM}`,
        backgroundColor: tokens.colorBrandBackground,
        borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
        position: 'sticky',
        top: 0,
        zIndex: 1,
        height: '48px',
        '@media (min-width: 460px)': {
            padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalL}`,
            height: '56px',
        },
        '@media (min-width: 768px)': {
            padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalXL}`,
            height: '64px',
        },
    },
    navTitleContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: tokens.spacingHorizontalS,
        flexGrow: 1,
        margin: 0,
        order: 2, // Move title to middle
    },
    menuButton: {
        color: tokens.colorNeutralForeground1,
        cursor: 'pointer',
        padding: tokens.spacingHorizontalXS,
        marginRight: tokens.spacingHorizontalS,
        order: 1, // Move menu button to start
        ':hover': {
            color: tokens.colorNeutralForeground1Hover,
        },
    },
    drawerContent: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalM,
        padding: tokens.spacingVerticalM,
        width: '100%', // Change from minWidth to width
        maxWidth: '320px', // Add maxWidth for larger screens
        boxSizing: 'border-box', // Ensure padding is included in width
    },
    themeToggle: {
        display: 'flex',
        alignItems: 'center',
        gap: tokens.spacingHorizontalS,
        width: '100%',
        padding: tokens.spacingVerticalS,
    },
    inputContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalM,
        width: '100%', // Ensure inputs take full width
    },
    refreshButton: {
        width: '100%',
        marginTop: tokens.spacingVerticalS,
    },
    closeButton: {
        color: tokens.colorNeutralForeground1,
        cursor: 'pointer',
        ':hover': {
            color: tokens.colorNeutralForeground1Hover,
        },
    },
});

interface NavbarProps {
    isDarkTheme: boolean;
    onThemeChange: (ev: React.SyntheticEvent, data: { checked: boolean }) => void;
    userName: string;
    onUserNameChange: (value: string) => void;
    language: StaffLanguage;
    onLanguageChange: (value: StaffLanguage) => void;
    onRefresh: () => void;
}

export const Navbar: React.FC<NavbarProps> = ({
    isDarkTheme,
    onThemeChange,
    userName,
    onUserNameChange,
    language,
    onLanguageChange,
    onRefresh,
}) => {
    const styles = useStyles();
    const [isOpen, setIsOpen] = useState(false);

    return (
        <div className={styles.stickyHeader}>
            <nav className={styles.navbar}>
                <Navigation24Regular 
                    className={styles.menuButton} 
                    onClick={() => setIsOpen(true)}
                />
                <div className={styles.navTitleContainer}>
                    <Title3>Anime Cross Reference</Title3>
                </div>
            </nav>

            <Drawer
                open={isOpen}
                onOpenChange={(_, { open }) => setIsOpen(open)}
                position="start"
                modalType="modal"
                style={{ maxWidth: '100vw' }} // Prevent drawer from overflowing viewport
            >
                <DrawerHeader>
                    <DrawerHeaderTitle 
                        action={
                            <Button
                                appearance="subtle"
                                icon={<Dismiss24Regular />}
                                onClick={() => setIsOpen(false)}
                            />
                        }
                    >
                        Settings
                    </DrawerHeaderTitle>
                </DrawerHeader>
                <DrawerBody>
                    <div className={styles.drawerContent}>
                        <div className={styles.themeToggle}>
                            <Switch 
                                checked={isDarkTheme}
                                onChange={onThemeChange}
                                label={isDarkTheme ? "Dark Theme" : "Light Theme"}
                            />
                        </div>
                        <div className={styles.inputContainer}>
                            <Input
                                value={userName}
                                onChange={(e) => onUserNameChange(e.target.value)}
                                placeholder="Username"
                                size="medium"
                            />
                            <Dropdown
                                value={language}
                                onOptionSelect={(_, data) => 
                                    data.optionValue && onLanguageChange(data.optionValue as StaffLanguage)
                                }
                                size="medium"
                            >
                                {Object.values(StaffLanguage).map((lang) => (
                                    <Option key={lang} value={lang}>
                                        {lang}
                                    </Option>
                                ))}
                            </Dropdown>
                            <Button
                                className={styles.refreshButton}
                                appearance="primary"
                                icon={<ArrowClockwise24Regular />}
                                onClick={onRefresh}
                            >
                                Refresh
                            </Button>
                        </div>
                    </div>
                </DrawerBody>
            </Drawer>
        </div>
    );
};